import {useCallback, useMemo} from 'react'
import {Box, Slider, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  DEVICE_POSITION_Z_MAX,
  selectActiveScene,
  setDevicePositionZ,
} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── DeviceDepthPanel — Z-axis translation of the device ──────────
// Lets the user push the device fore/aft relative to 3D text and
// sprites to compose depth-stacked scenes.

const DeviceDepthPanel = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const devicePositionZ = useAppSelector((s) => selectActiveScene(s).devicePositionZ)

  const handleChange = useCallback(
    (_: Event, value: number | number[]) => dispatch(setDevicePositionZ(value as number)),
    [dispatch],
  )
  const handleChangeCommitted = useCallback(() => dispatch(sealUndo()), [dispatch])

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Box sx={sx.root}>
      <Typography sx={sx.label}>{t('device.depth.title')}</Typography>
      <Box sx={sx.row}>
        <Slider
          value={devicePositionZ}
          min={0}
          max={DEVICE_POSITION_Z_MAX}
          step={1}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          sx={sx.slider}
          size="small"
        />
        <Typography sx={sx.value}>{devicePositionZ}</Typography>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    marginTop: '12px',
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: tokens.text.muted,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  slider: {
    flex: 1,
  },
  value: {
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text.primary,
    minWidth: 40,
    textAlign: 'right' as const,
  },
})

export default DeviceDepthPanel
